// machine.js — a thin, self-pacing wrapper around the wasm bridge.
//
// Owns the emulator lifecycle (stage floppy -> init), the run loop that paces
// emulated cycles to real 9.8304 MHz wall-clock, a "turbo boot" that runs
// uncapped until the machine is up (then locks to real time), and per-frame
// device polling that feeds the renderers. The bridge itself has NO internal
// throttle: pacing is entirely our responsibility (see wasm_bridge.c header).
//
// SCHEDULING: we drive the loop with requestAnimationFrame but ALSO arm a
// setTimeout on every tick; whichever fires first runs the tick and cancels its
// sibling. rAF is throttled/suspended in background tabs and can starve under
// headless virtual-time, so the setTimeout is the safety net — the loop keeps
// advancing whether or not the compositor paints.

import createVM86 from '../dist/vm86.mjs';

const GUEST_HZ = 9830400;
const FLOPPY_BYTES = 1474560;

// LCD region (used to detect "welcome screen drawn" for turbo-boot exit).
const LCD_ADDR = 0x80000;
const LCD_SIZE = (640 / 8) * 80; // 6400 bytes

// Turbo boot budgets.
const BOOT_WALL_BUDGET_MS = 28;      // wall time per turbo tick before yielding
// Extra instructions to run (uncapped) AFTER the welcome screen appears, so the
// printer finishes its power-on homing sweep before we lock to real time.
// Measured: video-on ~4M, LCD welcome ~5M, printer print-ready by ~+45M
// (≈35s of guest time — well past the ~8s homing window). ~50M total turbo.
const SETTLE_INSN = 45_000_000;
const TURBO_SLICE = 2_000_000;       // instr per slice during turbo
// Hard per-tick slice caps. Under headless virtual-time performance.now() can
// freeze while synchronous JS runs, so a wall-time-only bound would spin
// forever; these caps guarantee each tick yields to the event loop.
const TURBO_MAX_SLICES = 10;         // <=20M instr/tick, then yield

// Realtime pacing budgets.
//
// Rate control (see _realtimeStep): the guest cycle counter is the truth, and
// we pace it to a wall-clock cycle target. The subtle failure mode we design
// against is BURST GRANULARITY, not average drift. Execution advances in
// discrete runSlice() bursts; whatever cycles a burst covers all land "at once"
// against any measurement window, so the burst size is the quantum of the
// measured rate. The old code sliced by a fixed INSTRUCTION count (200k), but at
// the idle welcome screen the guest runs ~7.5 cyc/insn, so one slice jumped
// ~1.5M cycles (~154 ms of guest time) — ~15% of a second — and the rate
// aliased ±1 burst per window into a wide swing. The cure is to (1) size each
// burst in CYCLES (converted to an instruction count via a smoothed cyc/insn
// estimate) so bursts stay small and uniform across phases, and (2) tick often
// so per-tick catch-up stays a small fraction of a second.
// Per-tick emulation budget. This must comfortably exceed the wall time needed
// to clear one tick's cycle deficit, or the loop chronically bails on the
// deadline with debt still owed — that residual debt accretes until the clamp
// drops it, depressing and roughening the rate. On slow hosts (headless CI runs
// the core at barely ~1.8x realtime) a tick's deficit already needs ~12 ms of
// emulation, so a 12 ms budget starved it; 25 ms leaves headroom to reach the
// target and stop early. When the target is reached each tick, cumulative cycles
// track the wall-clock target and any overshoot is bounded to one slice
// (~0.65%) — which is what keeps 1 s windows tight.
const RT_FRAME_BUDGET_MS = 25;       // safety cap on emulation work per tick
const RT_TICK_MS = 10;               // realtime scheduler cadence (~100 Hz net)
const RT_SLICE_CYCLE_CAP = 65_536;   // max guest cycles advanced per runSlice
const RT_MIN_INSN = 2_000;           // floor on instructions per runSlice
const RT_MAX_INSN = 200_000;         // ceiling on instructions per runSlice
const RT_MAX_REALTIME_ITERS = 64;    // safety cap on slices per tick
const RT_MAX_DEBT_S = 0.10;          // clamp backlog to ~100 ms (never forgive all)
const CPI_EMA_ALPHA = 0.15;          // smoothing for the cyc/insn estimate
const DISP_MHZ_ALPHA = 0.25;         // EMA weight for the *displayed* MHz (~1 s)
const RENDER_INTERVAL_MS = 15;       // throttle renderer to ~60fps

export class Machine {
  constructor() {
    this.mod = null;
    this.api = null;
    this.ready = false;

    // lifecycle / pacing state
    this.booting = true;
    this.booted = false;
    this.powered = true;     // false only while the front-panel POWER switch is OFF
    this._turbo = true;
    this._bootedInsn = 0;
    this._totalInsn = 0;
    this._scheduled = false;
    this._running = false;
    this._resetting = false; // guards RESET re-entrancy (button spam)
    this._floppyBytes = null; // last image staged into MEMFS (re-staged on power-on)

    // realtime anchor
    this._anchorWall = 0;
    this._anchorCycles = 0;
    this._rtSlice = RT_MIN_INSN;   // last instruction-count used (for pacedebug)
    this._cpi = 2.0;         // EMA of guest cycles per emulated instruction

    // fps / effective-speed tracking
    this._lastRenderWall = 0;
    this._fpsFrames = 0;
    this._fpsWindowStart = 0;
    this.fps = 0;
    this.effMhz = 0;        // raw (instantaneous-window) effective MHz
    this.dispMhz = 0;       // EMA-smoothed value shown in the UI
    this._speedCycles = 0;
    this._speedWall = 0;

    // ---- pacing instrumentation (?pacedebug=1) --------------------------
    // Per-second census of the realtime pacing loop.
    this._paceDebug =
      typeof location !== 'undefined' &&
      new URLSearchParams(location.search).get('pacedebug') === '1';
    this.paceLog = [];            // ring of the last ~180 per-second rows
    this._paceWallStart = 0;      // wall ms at the current census window start
    this._paceCycStart = 0;       // cycle count at the census window start
    this._paceSlices = 0;         // runSlice() calls this window
    this._paceBudgetHits = 0;     // ticks that hit the per-frame time budget
    this._paceReanchors = 0;      // debt clamps/forgives this window
    this._paceTicks = 0;          // realtime ticks this window

    // callbacks
    this._onFrame = [];
    this._onBooted = [];
    this._onError = [];

    // scratch (allocated once in load())
    this._buf = {};
  }

  onFrame(cb) { this._onFrame.push(cb); }
  onBooted(cb) { this._onBooted.push(cb); }
  onError(cb) { this._onError.push(cb); }

  // ---- lifecycle ---------------------------------------------------------

  async load(statusCb) {
    const say = statusCb || (() => {});
    say('Fetching floppy image…');
    const img = await this._fetchFloppy();
    await this._createInstance(img, say);
    return this;
  }

  // Build a fresh wasm module instance from scratch: instantiate the Emscripten
  // factory, wrap the bridge exports, allocate the poll scratch, stage the given
  // floppy image into MEMFS, and boot (vm86_init). This is the shared core of
  // both the initial load() and a POWER-ON cold boot (which drops the previous
  // module and calls this again — see powerOn()).
  async _createInstance(floppyBytes, say) {
    say('Loading emulator core…');
    // Resolve vm86.wasm / vm86.data next to the module (web/dist/). The Emscripten
    // data-package loader otherwise fetches vm86.data from the site root, so we
    // pin every runtime asset to dist/ relative to THIS module's URL.
    const distUrl = (p) => new URL('../dist/' + p, import.meta.url).href;
    this.mod = await createVM86({
      locateFile: (p) => distUrl(p),
      print: (l) => { if (/abort|RuntimeError/i.test(l)) this._fail(l); },
      printErr: (l) => { if (/abort|RuntimeError/i.test(l)) this._fail(l); },
      onAbort: (w) => this._fail(`wasm abort: ${w}`),
    });

    const M = this.mod;
    this.api = {
      init: M.cwrap('vm86_init', 'number', []),
      reset: M.cwrap('vm86_reset', 'number', []),
      runSlice: M.cwrap('vm86_run_slice', 'number', ['number']),
      cycles: M.cwrap('vm86_cycles', 'number', []),
      guestHz: M.cwrap('vm86_guest_hz', 'number', []),
      displayInfo: M.cwrap('vm86_display_info', null, ['number']),
      memPtr: M.cwrap('vm86_mem_ptr', 'number', []),
      memSize: M.cwrap('vm86_mem_size', 'number', []),
      keyMatrix: M.cwrap('vm86_key_matrix', null, ['number', 'number', 'number']),
      keyPc: M.cwrap('vm86_key_pc', null, ['number', 'number']),
      printerHead: M.cwrap('vm86_printer_head', 'number', ['number']),
      pagePoll: M.cwrap('vm86_printer_page_poll', 'number', ['number']),
      pageAck: M.cwrap('vm86_printer_page_ack', null, []),
      pageCount: M.cwrap('vm86_printer_page_count', 'number', []),
      snapshot: M.cwrap('vm86_printer_snapshot', null, []),
      printerKnob: M.cwrap('vm86_printer_knob', 'number', ['number']),
      printerSetTearBase: M.cwrap('vm86_printer_set_tear_base', null, ['number']),
      leds: M.cwrap('vm86_leds', null, ['number']),
      beeper: M.cwrap('vm86_beeper', null, ['number']),
      floppyStage: M.cwrap('vm86_floppy_stage', 'number', ['number', 'number']),
      floppySwap: M.cwrap('vm86_floppy_swap', 'number', ['number', 'number']),
      floppyExport: M.cwrap('vm86_floppy_export', 'number', ['number']),
      free: M.cwrap('vm86_free', null, ['number']),
      fddBusy: M.cwrap('vm86_fdd_busy', 'number', []),
    };

    // Reusable scratch buffers for the out-parameter getters.
    this._buf = {};
    this._buf.info = M._malloc(8 * 4);
    this._buf.head = M._malloc(11 * 4);
    this._buf.meta = M._malloc(10 * 4);
    this._buf.leds = M._malloc(4);
    this._buf.beeper = M._malloc(4 * 4);

    // Stage the floppy BEFORE init (init opens /floppy.img). Remember the image
    // so a power-cycle re-stages the SAME disk that is "physically" in the drive.
    if (!this._stageFloppy(floppyBytes)) throw new Error('failed to stage floppy image');
    this._floppyBytes = floppyBytes;

    say('Booting MS-2401H…');
    const rc = this.api.init();
    if (rc !== 0) throw new Error(`vm86_init failed (rc=${rc})`);

    this.ready = true;
    this._memBase = this.api.memPtr();
    return this;
  }

  async _fetchFloppy() {
    const res = await fetch('./floppy.img', { cache: 'no-store' });
    if (!res.ok) throw new Error(`floppy fetch failed: HTTP ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  }

  _stageFloppy(bytes) {
    const p = this.mod._malloc(bytes.length);
    this.mod.HEAPU8.set(bytes, p);
    const rc = this.api.floppyStage(p, bytes.length);
    this.mod._free(p);
    return rc === 0;
  }

  _fail(msg) {
    if (this._failed) return;
    this._failed = true;
    for (const cb of this._onError) cb(msg);
  }

  // ---- heap access for renderers ----------------------------------------

  heap() { return this.mod.HEAPU8; } // re-read: reassigned on memory growth
  memBase() { return this._memBase; }

  // ---- run loop ----------------------------------------------------------

  start() {
    if (this._running) return;
    this._running = true;
    this._fpsWindowStart = performance.now();
    this._speedWall = performance.now();
    this._speedCycles = this.api.cycles();
    this._schedule();
  }

  _schedule() {
    if (this._scheduled || !this._running) return;
    this._scheduled = true;
    let fired = false;
    const run = () => {
      if (fired) return;
      fired = true;
      this._scheduled = false;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      this._tick();
    };
    // Single tick per scheduling cycle: rAF and the timer both point at the same
    // `run`, the first to fire executes exactly once (the `fired` latch) and
    // cancels its sibling — so this is single-source per tick, never a double
    // fire. During turbo we burn instructions ASAP (0ms). In realtime we tick at
    // ~RT_TICK_MS so per-tick catch-up stays a small fraction of a second (fine
    // burst granularity); the timer is also the net that keeps us advancing when
    // rAF is starved (headless / background tab).
    const raf = requestAnimationFrame(run);
    const timer = setTimeout(run, this._turbo ? 0 : RT_TICK_MS);
  }

  _tick() {
    // Bail if the loop was stopped (pause / POWER OFF) after this tick was
    // already scheduled: without this, one in-flight tick would still run a
    // step + render AFTER power-off, repainting the displays we just blanked
    // (and clobbering the 'OFF' status). A no-op here also stops re-scheduling.
    if (this._failed || !this._running) return;
    try {
      if (this._turbo) this._turboStep();
      else this._realtimeStep();
      this._maybeRender();
    } catch (e) {
      // A renderer throwing must not kill the loop; log once and keep pacing.
      console.error('[ms2401] tick error', e);
      if (/wasm|abort|RuntimeError|memory/i.test(String(e))) {
        this._fail(String(e && e.stack ? e.stack : e));
        return;
      }
    }
    this._schedule();
  }

  _turboStep() {
    const deadline = performance.now() + BOOT_WALL_BUDGET_MS;
    let slices = 0;
    do {
      this.api.runSlice(TURBO_SLICE);
      this._totalInsn += TURBO_SLICE;
      slices++;
    } while (slices < TURBO_MAX_SLICES && performance.now() < deadline);

    if (!this.booted && this._lcdHasContent()) {
      this.booted = true;
      this._bootedInsn = this._totalInsn;
    }
    // Once the welcome screen is up, keep running turbo for a settle window so
    // the printer completes its homing sweep, then lock to real-time pacing.
    if (this.booted && this._totalInsn - this._bootedInsn >= SETTLE_INSN) {
      this._enterRealtime();
    }
  }

  _enterRealtime() {
    this._turbo = false;
    this.booting = false;
    this._anchorWall = performance.now();
    this._anchorCycles = this.api.cycles();
    this._paceWallStart = this._anchorWall;
    this._paceCycStart = this._anchorCycles;
    for (const cb of this._onBooted) cb(this);
  }

  _realtimeStep() {
    const now = performance.now();
    const cycPerMs = GUEST_HZ / 1000;
    let target = this._anchorCycles + (now - this._anchorWall) * cycPerMs;
    let cur = this.api.cycles();

    // Debt clamp. If we've fallen far behind (tab hidden, GC pause, slow
    // machine) don't spiral — but don't forgive the whole debt to "now" either,
    // because that backlog would then be caught up in one giant overshooting
    // burst (the very granularity we're avoiding). Instead pin the anchor so at
    // most RT_MAX_DEBT_S of catch-up remains: bounded, smooth catch-up.
    const maxDebt = GUEST_HZ * RT_MAX_DEBT_S;
    if (target - cur > maxDebt) {
      this._anchorWall = now;
      this._anchorCycles = cur + maxDebt;
      target = this._anchorCycles;
      this._paceReanchors++;
    }

    const deadline = now + RT_FRAME_BUDGET_MS;
    let iters = 0;
    let budgetHit = false;
    while (iters < RT_MAX_REALTIME_ITERS) {
      cur = this.api.cycles();
      const remaining = target - cur;
      if (remaining <= 0) break;                       // reached the target
      if (performance.now() >= deadline) { budgetHit = true; break; }
      // Size this burst in CYCLES (bounded), then convert to an instruction
      // count through the smoothed cyc/insn estimate. Because the cap is in
      // cycles, bursts stay uniformly small whether the guest is spinning in a
      // HLT idle (~7.5 cyc/insn), servicing print NMIs (~5.5), or in its main
      // loop (~2) — which is what holds the measured rate flat sub-second.
      const wantCycles = Math.min(remaining, RT_SLICE_CYCLE_CAP);
      let insn = Math.round(wantCycles / this._cpi);
      if (insn < RT_MIN_INSN) insn = RT_MIN_INSN;
      else if (insn > RT_MAX_INSN) insn = RT_MAX_INSN;
      const advanced = this.api.runSlice(insn);
      iters++;
      this._paceSlices++;
      this._rtSlice = insn;
      if (advanced > 0) {
        const cpi = advanced / insn;
        this._cpi += (cpi - this._cpi) * CPI_EMA_ALPHA;
        if (this._cpi < 0.5) this._cpi = 0.5;          // guard the estimate
      }
    }
    if (budgetHit) this._paceBudgetHits++;
    this._paceTicks++;
    this._paceCensus(now);
  }

  // Emit one per-second row of pacing stats (only when ?pacedebug=1). The
  // effective MHz here is measured over a full ~1s window straight off the
  // guest cycle counter, so it reflects the REAL rate, not the UI's smoothed
  // display — the point is to see whether the rate itself is steady.
  _paceCensus(now) {
    if (!this._paceDebug) return;
    const dw = now - this._paceWallStart;
    if (dw < 1000) return;
    const cur = this.api.cycles();
    const dc = cur - this._paceCycStart;
    const mhz = dc / (dw * 1000); // cycles/ms -> MHz
    const row = {
      t: +(now / 1000).toFixed(1),
      cycles: dc,
      wallMs: +dw.toFixed(1),
      mhz: +mhz.toFixed(3),
      slices: this._paceSlices,
      ticks: this._paceTicks,
      budgetHits: this._paceBudgetHits,
      reanchors: this._paceReanchors,
      rtSlice: this._rtSlice,
      cpi: +this._cpi.toFixed(2),
    };
    this.paceLog.push(row);
    if (this.paceLog.length > 180) this.paceLog.shift();
    console.log('[pacedebug] ' + JSON.stringify(row));
    this._paceWallStart = now;
    this._paceCycStart = cur;
    this._paceSlices = 0;
    this._paceTicks = 0;
    this._paceBudgetHits = 0;
    this._paceReanchors = 0;
  }

  _lcdHasContent() {
    const heap = this.mod.HEAPU8;
    const base = this._memBase + LCD_ADDR;
    let n = 0;
    for (let i = 0; i < LCD_SIZE; i += 4) if (heap[base + i]) { if (++n > 12) return true; }
    return false;
  }

  // ---- polling / render dispatch ----------------------------------------

  _maybeRender() {
    const now = performance.now();
    if (now - this._lastRenderWall < RENDER_INTERVAL_MS) return;
    this._lastRenderWall = now;

    // fps
    this._fpsFrames++;
    if (now - this._fpsWindowStart >= 500) {
      this.fps = (this._fpsFrames * 1000) / (now - this._fpsWindowStart);
      this._fpsFrames = 0;
      this._fpsWindowStart = now;
    }
    // effective guest speed (MHz) from cycles/wall between renders
    const cyc = this.api.cycles();
    const dw = now - this._speedWall;
    if (dw >= 250) {
      this.effMhz = (cyc - this._speedCycles) / (dw * 1000); // cycles/ms -> MHz
      // EMA-smooth for the DISPLAY only (~1 s time constant over 250 ms
      // samples). This calms the readout; it does NOT stand in for proof — the
      // real rate is measured independently over 1 s windows by ?pacedebug,
      // straight off the cycle counter, so the display can't hide instability.
      this.dispMhz = this.dispMhz === 0
        ? this.effMhz
        : this.dispMhz + (this.effMhz - this.dispMhz) * DISP_MHZ_ALPHA;
      this._speedCycles = cyc;
      this._speedWall = now;
    }

    const snap = this._snapshot(cyc);
    for (const cb of this._onFrame) cb(this, snap);
  }

  _snapshot(cyc) {
    const M = this.mod;
    const gi = (p, i) => M.getValue(p + i * 4, 'i32');
    const gu = (p, i) => M.getValue(p + i * 4, 'i32') >>> 0;

    this.api.displayInfo(this._buf.info);
    const info = this._buf.info;
    const display = {
      mode: gi(info, 0), width: gi(info, 1), height: gi(info, 2),
      vram: gu(info, 3), stride: gi(info, 4), start: gi(info, 5),
      page: gi(info, 6), window: gu(info, 7),
    };

    const valid = this.api.printerHead(this._buf.head) === 1;
    const h = this._buf.head;
    const head = {
      valid,
      x: gi(h, 0), y: gi(h, 1), maxX: gi(h, 2), ctrl70: gi(h, 3),
      spPhase: gi(h, 4), lfPhase: gi(h, 5),
      lesb: gi(h, 6), pesb: gi(h, 7), thsn: gi(h, 8),
      busy: gi(h, 9), firing: gi(h, 10),
    };

    this.api.leds(this._buf.leds);
    const leds = [0, 1, 2, 3].map((i) => M.HEAPU8[this._buf.leds + i]);

    this.api.beeper(this._buf.beeper);
    const beeper = {
      enabled: gu(this._buf.beeper, 0), freq: gu(this._buf.beeper, 1),
      events: gu(this._buf.beeper, 2), eventFreq: gu(this._buf.beeper, 3),
    };

    return {
      cycles: cyc, fps: this.fps,
      mhz: this.dispMhz,      // smoothed value for the UI readout
      mhzRaw: this.effMhz,    // raw 250 ms-window rate (diagnostics)
      booting: this.booting, booted: this.booted,
      display, head, leds, beeper,
      fddBusy: this.fddBusy(),
    };
  }

  // Drain all pending printer pages, copying payloads out of the heap.
  drainPages() {
    const out = [];
    const M = this.mod;
    const gi = (p, i) => M.getValue(p + i * 4, 'i32');
    const gu = (p, i) => M.getValue(p + i * 4, 'i32') >>> 0;
    while (this.api.pageCount() > 0) {
      const dataPtr = this.api.pagePoll(this._buf.meta);
      if (!dataPtr) break;
      const m = this._buf.meta;
      const dataLen = gu(m, 9);
      // Copy the payload before ack releases the slot.
      const bytes = M.HEAPU8.slice(dataPtr, dataPtr + dataLen);
      out.push({
        index: gu(m, 0), width: gi(m, 1), height: gi(m, 2), stride: gu(m, 3),
        xMin: gi(m, 4), yMin: gi(m, 5), xMax: gi(m, 6), yMax: gi(m, 7),
        fires: gu(m, 8), dataLen, bytes,
      });
      this.api.pageAck();
    }
    return out;
  }

  requestSnapshot() { if (this.ready) this.api.snapshot(); }

  // Analog platen knob: roll the paper by delta LF steps (shared LF accumulator;
  // clamped at the tear base, touches no BIOS state). Returns the resulting LF.
  knob(delta) { return this.ready ? this.api.printerKnob(delta | 0) : 0; }

  // Rebase the knob's clamp floor to the paper's current leading edge (tear-off).
  setTearBase(lf) { if (this.ready) this.api.printerSetTearBase(lf | 0); }

  // ---- power / reset -----------------------------------------------------

  // Return pacing to cycle zero with no realtime anchor. Cold boot and warm
  // reset both re-run the turbo-boot settle window.
  _resetPacingState() {
    this.booting = true;
    this.booted = false;
    this._turbo = true;
    this._bootedInsn = 0;
    this._totalInsn = 0;
    this._anchorWall = 0;
    this._anchorCycles = 0;
    this._cpi = 2.0;
    this.fps = 0;
    this.effMhz = 0;
    this.dispMhz = 0;
    this._lastRenderWall = 0;
    this._fpsFrames = 0;
  }

  // Front-panel POWER switch -> OFF. Stops the pacing loop dead (displays go
  // dark, beeper falls silent — app.js paints the dark state). The wasm module
  // is kept resident but frozen; power-on drops it and builds a fresh one.
  powerOff() {
    if (!this.powered) return;
    this.powered = false;
    this._running = false;   // halts _schedule()/_tick()
  }

  // Front-panel POWER switch -> ON from OFF: a full COLD BOOT. The cleanest
  // correct model is a brand-new module — we drop the old instance (its ~35 MB
  // heap becomes GC garbage; one buffer churned per power cycle, acceptable) and
  // rebuild from scratch so no guest RAM, device, or CPU state can survive the
  // power interruption. The disk is "physical": we re-stage the image currently
  // in the drive (guest writes included when exportable), falling back to the
  // last staged bytes, then the default. Keeps turbo boot.
  async powerOn(statusCb) {
    if (this.powered) return;
    const say = statusCb || (() => {});

    // Grab the disk that is "in the drive" before tearing the old module down.
    let disk = null;
    try { disk = this.exportFloppy(); } catch (_) { disk = null; }
    if (!disk) disk = this._floppyBytes;

    // Drop the old instance. Nulling the references lets the whole wasm module
    // (heap ArrayBuffer + glue) be collected; there is no in-place free.
    this.ready = false;
    this.booted = false;
    this.mod = null;
    this.api = null;
    this._memBase = 0;
    this._failed = false;

    this._resetPacingState();
    if (!disk) disk = await this._fetchFloppy();
    await this._createInstance(disk, say);

    this.powered = true;
    this._running = false;
    this.start();
  }

  // Front-panel RESET button: a WARM restart of the guest via the hardware
  // RESET line (vm86_reset -> ms2401h_reset), which clears RAM + re-inits every
  // device but PRESERVES battery-backed SRAM, WITHOUT a powered-off interlude
  // and WITHOUT rebuilding the module. Re-enters the turbo boot so the welcome
  // banner redraws and the printer re-homes, exactly like power-on but faster.
  // Returns false (no-op) if not ready or a reset is already in flight.
  reset() {
    if (!this.ready || this._resetting || !this.powered) return false;
    this._resetting = true;
    const rc = this.api.reset();
    if (rc !== 0) { this._resetting = false; return false; }
    this._memBase = this.api.memPtr();  // re-read (unchanged, but defensive)
    this._resetPacingState();
    this._resetting = false;
    if (!this._running) { this._running = true; }
    this._schedule();
    return true;
  }

  // ---- pause / resume ----------------------------------------------------

  pause() { this._running = false; }
  resume() {
    if (this._running || !this.ready || !this.powered) return;
    this._running = true;
    if (!this._turbo) {
      // Re-anchor so the paused wall time isn't charged as emulation debt.
      this._anchorWall = performance.now();
      this._anchorCycles = this.api.cycles();
    }
    this._schedule();
  }
  get paused() { return this.ready && !this._running; }

  // ---- input -------------------------------------------------------------

  keyPc(scancode, pressed) { this.api.keyPc(scancode & 0xffff, pressed ? 1 : 0); }
  keyMatrix(row, col, pressed) { this.api.keyMatrix(row, col, pressed ? 1 : 0); }

  // Press a matrix cell NOW and return a release() closure that guarantees the
  // line stays down for at least `minHoldMs` of WALL time before it lifts. The
  // BIOS scans the matrix on its own INT43h cadence and needs the key held
  // across at least one scan/debounce window (~200ms) to latch it; a physical
  // key satisfies this naturally, but a tap on the soft keyboard (or an instant
  // press+release) would drop the line before any scan sees it. Both the soft
  // keyboard and pressPrintKey() funnel through here so physical and synthetic
  // input can't diverge on hold timing. Calling release() more than once is a
  // no-op.
  matrixHold(row, col, minHoldMs = 250) {
    const pressWall = performance.now();
    this.keyMatrix(row, col, true);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const wait = Math.max(0, pressWall + minHoldMs - performance.now());
      setTimeout(() => this.keyMatrix(row, col, false), wait);
    };
  }

  // The print key is matrix row 10, col 3 (拷屏; verified by bridge-test.mjs).
  // Uses the shared matrixHold path so its hold discipline matches soft keys.
  pressPrintKey(holdMs = 300) {
    this.matrixHold(10, 3, holdMs)();
  }

  // ---- floppy ------------------------------------------------------------

  swapFloppy(bytes) {
    const p = this.mod._malloc(bytes.length);
    this.mod.HEAPU8.set(bytes, p);
    const rc = this.api.floppySwap(p, bytes.length);
    this.mod._free(p);
    // Remember the disk now "in the drive" so a power-cycle re-stages it.
    if (rc === 0) this._floppyBytes = bytes.slice();
    return rc === 0;
  }

  // Live FDC-busy accessor (true while a read/write is in flight). Used both by
  // the per-frame snapshot and by diskman's pre-swap re-check so a write is never
  // committed on top of an in-progress guest disk operation.
  fddBusy() { return this.ready && this.api.fddBusy() === 1; }

  exportFloppy() {
    const lenPtr = this.mod._malloc(4);
    const dataPtr = this.api.floppyExport(lenPtr);
    const len = this.mod.getValue(lenPtr, 'i32') >>> 0;
    this.mod._free(lenPtr);
    if (!dataPtr || !len) { if (dataPtr) this.api.free(dataPtr); return null; }
    const out = this.mod.HEAPU8.slice(dataPtr, dataPtr + len);
    this.api.free(dataPtr);
    return out;
  }

  get expectedFloppyBytes() { return FLOPPY_BYTES; }
}
